import { SdkContext } from '../internal'
import type { Badge, BadgeHolder, BadgeRuleInput, BadgeVisibility, MemberBadge } from '@/types/badges'

export interface BadgeInput {
  name?: string
  description?: string | null
  imageUrl?: string
  visibility?: BadgeVisibility
  /** F-189: an object sets the rule and restarts its count; `null` removes it; absent keeps it. */
  rule?: BadgeRuleInput | null
}

/** The result of giving a badge to one person: `changed: false` when the person already held it. */
export interface BadgeAward {
  changed: boolean
  badgeId: string
  memberId: string
  grantId?: string
}

export interface BadgeGrantResult {
  awarded: number
  alreadyHeld: number
  failed: number
  results: Array<{ memberId: string; changed: boolean; grantId?: string; error?: string }>
}

const listOf = <T,>(r: unknown): T[] => (Array.isArray(r) ? r : ((r as { items?: T[] } | null)?.items ?? [])) as T[]

/**
 * F-148 badges. The catalog and the grants are owner and admin only (the API
 * answers 403 to anyone else); a person's badges are read by anyone in the
 * community, with the private ones filtered by the server.
 */
export class BadgesResource {
  constructor(private ctx: SdkContext) {}

  async list(): Promise<Badge[]> {
    return listOf<Badge>(await this.ctx.call({ httpMethod: 'GET', path: '/badges' }))
  }

  async get(id: string): Promise<Badge> {
    return this.ctx.call({ httpMethod: 'GET', path: '/badges/{id}' }, { id })
  }

  async create(body: BadgeInput & { name: string; imageUrl: string }): Promise<Badge> {
    return this.ctx.call({ httpMethod: 'POST', path: '/badges', hasBody: true }, { body })
  }

  async update(id: string, body: BadgeInput): Promise<Badge> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/badges/{id}', hasBody: true }, { id, body })
  }

  /** Soft delete: the badge leaves the catalog, the profiles and the ranking; `holders` is how many had it. */
  async remove(id: string): Promise<{ id: string; holders: number }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/badges/{id}' }, { id })
  }

  /** The image goes first and comes back as a URL for `create` or `update` (PNG of up to 512×512 px). */
  async uploadImage(file: File): Promise<{ url: string }> {
    const fd = new FormData()
    fd.append('image', file)
    return this.ctx.call({ httpMethod: 'POST', path: '/badges/image', hasBody: true }, { body: fd })
  }

  async holders(id: string, limit = 50): Promise<BadgeHolder[]> {
    return listOf<BadgeHolder>(await this.ctx.call({ httpMethod: 'GET', path: '/badges/{id}/holders' }, { id, query: { limit } }))
  }

  /** "Conceder a…": up to 100 people at once; who already holds it is counted, not changed. */
  async grant(id: string, memberIds: string[], reason?: string | null): Promise<BadgeGrantResult> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/badges/{id}/grants', hasBody: true },
      { id, body: reason ? { memberIds, reason } : { memberIds } }
    )
  }

  async ofMember(memberId: string): Promise<MemberBadge[]> {
    return listOf<MemberBadge>(await this.ctx.call({ httpMethod: 'GET', path: '/members/{memberId}/badges' }, { memberId }))
  }

  async award(memberId: string, badgeId: string, reason?: string | null): Promise<BadgeAward> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/members/{memberId}/badges', hasBody: true },
      { memberId, body: reason ? { badgeId, reason } : { badgeId } }
    )
  }

  /** Taking a badge back never notifies the person. */
  async revoke(memberId: string, badgeId: string): Promise<BadgeAward> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/members/{memberId}/badges/{badgeId}' }, { memberId, badgeId })
  }
}
